import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Users, Shield, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { guideAPI, leadAPI, seedAPI } from '../lib/api';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import BhumiLogo from '../components/BhumiLogo';
import { getField } from '../lib/localize';

const LandingPage = () => {
  const [guides, setGuides] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { settings } = useSite();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  useEffect(() => {
    const initData = async () => {
      try {
        // First try to seed database
        await seedAPI.seed();
      } catch (err) {
        console.log('Seed skipped (already seeded):', err.message);
      }
      
      // Fetch guides
      try {
        const res = await guideAPI.getAll();
        setGuides(res.data.slice(0, 3));
      } catch (err) {
        console.error('Failed to fetch guides:', err);
      }
    };
    initData();
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await leadAPI.create({
        name: 'Newsletter Subscriber',
        email,
        phone: '',
        message: 'Newsletter subscription from landing page',
      });
      toast.success(t('common.success'));
      setEmail('');
    } catch (err) {
      toast.error(t('common.error'));
    }
    setLoading(false);
  };

  const features = [
    {
      icon: FileText,
      title: t('features.completeInfo'),
      description: t('features.completeInfoDesc'),
    },
    {
      icon: Users,
      title: t('features.verifiedProviders'),
      description: t('features.verifiedProvidersDesc'),
    },
    {
      icon: Shield,
      title: t('features.trustedPlatform'),
      description: t('features.trustedPlatformDesc'),
    },
  ];

  const stats = [
    { value: language === 'en' ? '50+' : '৫০+', label: t('stats.guides') },
    { value: language === 'en' ? '100+' : '১০০+', label: t('stats.providers') },
    { value: language === 'en' ? '10,000+' : '১০,০০০+', label: t('stats.users') },
  ];

  const categoryIcons = {
    documentation: FileText,
    legal: Shield,
    fees: CheckCircle,
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
      <SEO 
        title={language === 'en' ? 'Bangladesh Land Information Platform' : 'বাংলাদেশের জমি সংক্রান্ত তথ্য প্ল্যাটফর্ম'}
        description={language === 'en' 
          ? 'Complete guide on land registration, Namjari, Khatian, deeds, and verified service providers in Bangladesh.'
          : 'বাংলাদেশের জমি সংক্রান্ত সম্পূর্ণ তথ্য। নামজারি, খতিয়ান, দলিল রেজিস্ট্রেশন এবং যাচাইকৃত সেবাদাতা খুঁজুন।'
        }
        path="/"
      />
      <Navbar isDark={isDark} />

      {/* Hero Section */}
      <section className={`relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-16 ${isDark ? '' : 'bg-gradient-to-b from-[#E8F5E9] to-white'}`}>
        {/* Background Image */}
        <div 
          className={`absolute inset-0 ${isDark ? 'opacity-20' : 'opacity-10'}`}
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1706206896606-8067279b7feb?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Overlay */}
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/60 to-[#0A0A0A]' : 'bg-gradient-to-b from-white/60 via-white/80 to-white'}`} />
        
        {/* Pattern */}
        <div className="absolute inset-0 hero-pattern" />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 text-center">
          <div className="animate-fade-in">
            {/* Logo */}
            <div className="flex justify-center mb-6 md:mb-8">
              <BhumiLogo size="xl" showText={false} isDark={isDark} />
            </div>

            {/* Badge */}
            <div className={`inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-full ${isDark ? 'bg-white/10 border border-white/20' : 'bg-[#2E7D32]/10 border border-[#2E7D32]/20'} mb-6 md:mb-8`}>
              <span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse" />
              <span className={`text-xs md:text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{t('landing.badge')}</span>
            </div>

            {/* Main Heading */}
            <h1 className={`text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="hero-title">
              {t('landing.heroTitle1')}
              <br />
              <span className="gradient-text">{t('landing.heroTitle2')}</span>
            </h1>

            {/* Subtitle */}
            <p className={`text-base sm:text-lg md:text-xl max-w-2xl mx-auto mb-8 md:mb-10 px-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('landing.heroSubtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-10 md:mb-16">
              <Link to="/guides" className="w-full sm:w-auto">
                <Button 
                  data-testid="hero-cta-primary"
                  className="w-full sm:w-auto bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold shadow-lg btn-glow transition-all duration-300 hover:-translate-y-1"
                >
                  {t('landing.ctaStart')}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/about" className="w-full sm:w-auto">
                <Button 
                  data-testid="hero-cta-secondary"
                  variant="outline"
                  className={`w-full sm:w-auto border-2 rounded-full px-6 md:px-8 py-5 md:py-6 text-base md:text-lg font-semibold transition-all duration-300 ${isDark ? 'border-white/30 text-white hover:bg-white/10' : 'border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32]/10'}`}
                >
                  {t('landing.ctaLearnMore')}
                </Button>
              </Link>
            </div>

            {/* Email Capture */}
            <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto px-4">
              <div className={`flex gap-2 p-1.5 md:p-2 rounded-full ${isDark ? 'bg-white/10 border border-white/20' : 'bg-gray-100 border border-gray-200'}`}>
                <Input
                  type="email"
                  placeholder={t('landing.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`flex-1 bg-transparent border-0 focus-visible:ring-0 text-sm md:text-base h-10 md:h-auto ${isDark ? 'text-white placeholder:text-gray-500' : 'text-gray-900 placeholder:text-gray-500'}`}
                  data-testid="email-input"
                />
                <Button 
                  type="submit"
                  disabled={loading}
                  className="bg-[#FBC02D] hover:bg-[#F9A825] text-gray-900 rounded-full px-4 md:px-6 font-semibold text-sm md:text-base"
                  data-testid="email-submit"
                >
                  {loading ? '...' : t('landing.subscribe')}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
          <div className={`w-6 h-10 rounded-full border-2 flex items-start justify-center p-2 ${isDark ? 'border-white/30' : 'border-gray-400'}`}>
            <div className={`w-1 h-2 rounded-full ${isDark ? 'bg-white/50' : 'bg-gray-400'}`} />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('landing.whyChoose')} <span className="text-[#FBC02D]">{settings.site_name}</span>{t('landing.whyChooseUs')}
            </h2>
            <p className={`text-sm md:text-base max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('landing.whyChooseDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className={`rounded-2xl p-6 md:p-8 card-hover ${isDark ? 'glass' : 'bg-gray-50 border border-gray-200'}`}
                data-testid={`feature-card-${feature.title}`}
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#2E7D32]/20 flex items-center justify-center mb-4 md:mb-6">
                  <feature.icon className="text-[#2E7D32]" size={24} />
                </div>
                <h3 className={`text-lg md:text-xl font-semibold mb-2 md:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{feature.title}</h3>
                <p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className={`py-12 md:py-16 ${isDark ? 'bg-[#0D330F]' : 'bg-[#E8F5E9]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-4 md:gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className={`text-2xl sm:text-4xl md:text-5xl font-bold font-data mb-1 md:mb-2 ${isDark ? 'text-[#FBC02D]' : 'text-[#2E7D32]'}`}>
                  {stat.value}
                </div>
                <div className={`text-xs sm:text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Guides */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#0A0A0A]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8 md:mb-12">
            <div>
              <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('landing.popularGuides')}
              </h2>
              <p className={`text-sm md:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('landing.popularGuidesDesc')}</p>
            </div>
            <Link to="/guides">
              <Button variant="ghost" className={`text-sm md:text-base ${isDark ? 'text-[#FBC02D] hover:text-[#F9A825]' : 'text-[#2E7D32] hover:text-[#1B5E20]'} hover:bg-transparent`}>
                {t('landing.viewAll')} <ChevronRight size={18} />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {guides.map((guide, index) => {
              const IconComponent = categoryIcons[guide.category] || FileText;
              return (
                <Link
                  key={guide.id}
                  to={`/guides/${guide.slug}`}
                  className={`rounded-2xl p-4 md:p-6 card-hover animate-slide-up stagger-${index + 1} ${isDark ? 'glass' : 'bg-gray-50 border border-gray-200'}`}
                  data-testid={`guide-card-${index}`}
                >
                  <div className="flex items-center gap-3 mb-3 md:mb-4">
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#2E7D32]/20 flex items-center justify-center">
                      <IconComponent className="text-[#2E7D32]" size={18} />
                    </div>
                    <span className="text-xs text-[#FBC02D] uppercase tracking-wider font-medium">
                      {t(`categories.${guide.category}`)}
                    </span>
                  </div>
                  <h3 className={`text-base md:text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{getField(guide, 'title', language)}</h3>
                  <p className={`text-xs md:text-sm line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{getField(guide, 'summary', language)}</p>
                  <div className="mt-3 md:mt-4 flex items-center text-[#2E7D32] text-xs md:text-sm font-medium">
                    {t('landing.readMore')} <ArrowRight size={14} className="ml-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1767481626814-d5cf877d1a82?crop=entropy&cs=srgb&fm=jpg&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className={`absolute inset-0 ${isDark ? 'bg-[#0A0A0A]/90' : 'bg-white/90'}`} />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('landing.haveProblem')}
          </h2>
          <p className={`text-base md:text-xl mb-8 md:mb-10 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t('landing.connectProviders')}
          </p>
          <Link to="/services">
            <Button 
              className="bg-[#FBC02D] hover:bg-[#F9A825] text-gray-900 rounded-full px-8 md:px-10 py-5 md:py-6 text-base md:text-lg font-semibold shadow-lg transition-all duration-300 hover:-translate-y-1"
              data-testid="cta-services"
            >
              {t('landing.findProviders')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default LandingPage;
